/**
 * NotificationProvider — Phase 5.0.
 * Owns flashbar items + displayNotification. Extracted from FintechContextProvider.
 * Behaviour-preserving copy of the legacy implementation.
 */
import { FlashbarProps, Link } from '@amzn/awsui-components-core';
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

export interface NotificationContextValue {
  flashbarItems: FlashbarProps.MessageDefinition[];
  displayNotification: (status: FlashbarProps.Type, message: string | React.ReactNode, linkMessage?: string) => void;
}

const defaultValue: NotificationContextValue = {
  flashbarItems: [],
  displayNotification: () => {}
};

const NotificationContext = createContext<NotificationContextValue>(defaultValue);

export const useNotification = () => useContext(NotificationContext);

interface Props {
  children: React.ReactNode;
}

export const NotificationProvider = ({ children }: Props) => {
  const [flashbarItems, setFlashbarItems] = useState<FlashbarProps.MessageDefinition[]>([]);

  const displayNotification = useCallback<NotificationContextValue['displayNotification']>((status, message, linkMessage) => {
    setFlashbarItems([
      {
        type: status,
        content: (
          <>
            {message}
            {linkMessage && (
              <>
                {' or '}
                <Link variant="primary" target="_blank" href={linkMessage} color="inverted" fontSize="body-m">
                  submit a ticket
                </Link>
              </>
            )}
          </>
        ),
        dismissible: true,
        dismissLabel: 'Dismiss message',
        onDismiss: () => setFlashbarItems([]),
        id: 'message_1'
      }
    ]);
  }, []);

  const value = useMemo(() => ({ flashbarItems, displayNotification }), [flashbarItems, displayNotification]);

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};
